import { useEffect, useRef, useState, type CSSProperties } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Icon } from "../components/Icon.js";
import { Btn } from "../components/index.js";
import { ReviewGate } from "../components/ReviewGate.js";
import { useCloseTo } from "../lib/useCloseTo.js";
import { useSurface } from "../lib/useSurface.js";
import { useWakeLock } from "../lib/useWakeLock.js";
import { sendVideoOrQueue } from "../lib/offline.js";
import { rememberCaptureOrigin } from "../lib/captureOrigin.js";
import { mmss } from "../lib/demo.js";

/**
 * Screen C — video capture. Uses getUserMedia + MediaRecorder for real recording.
 *
 * The recorder is a 4-phase state machine (idle → recording ⇄ paused → review) driven by a
 * SINGLE MediaRecorder instance: 一時停止/再開 (rec.pause()/resume()) splice fragmented takes into
 * ONE continuous blob with a single timeline (jump-cuts), so the saved bytes/container are byte-for-
 * byte identical to a straight recording → the AI pipeline (fps:1 analysis, keyframe candidates,
 * clip ranges, transcription) sees unchanged input = zero precision impact.
 *
 * On 完了 we DON'T auto-send: a confirmation gate (送る / 撮り直す / 破棄) lets the operator弾く
 * 誤録画 before any upload/Gemini cost. Only 『送る』 runs the init → PUT → complete handshake; if the
 * API is unreachable it parks the recording in the offline queue and advances to an honest
 * processing screen. Recording is capped at VIDEO_MAX_ACTIVE_MS of ACTIVE time (pause excluded).
 */

// ---------- capture limits / quality (UI-only; not part of the server data contract) ----------
/** Max ACTIVE recording time (pause time excluded). Auto-finish on reach. 15 min — the pipeline
 *  handles long/large recordings (chunked upload + chunked Gemini transfer + SSE analysis). */
const VIDEO_MAX_ACTIVE_MS = 900_000;
/** Show the .hl warning when remaining active time drops to/below this. 30 s. */
const VIDEO_WARN_REMAINING_MS = 30_000;
/** Default (standard) video bitrate cap. */
const VIDEO_BITRATE_STANDARD = 4_000_000;
/** High-detail (tiny-label industries) video bitrate cap — opt-in via Settings. */
const VIDEO_BITRATE_HIRES = 5_000_000;
/** Narration audio bitrate — the transcription lifeline; never go below this. */
const AUDIO_BITRATE = 128_000;

/** Best-supported recording container/codecs, in preference order. mp4/avc1 first for iOS Safari. */
const MIME_CANDIDATES = [
  "video/mp4;codecs=avc1.42E01E,mp4a.40.2",
  "video/mp4;codecs=avc1",
  "video/mp4",
  "video/webm;codecs=vp9,opus",
  "video/webm;codecs=vp8,opus",
  "video/webm",
];

/** Camera constraints — request 1080p/30fps best-effort (ideal/max only so no device fails). */
const cameraConstraints: MediaStreamConstraints = {
  video: { facingMode: "environment", width: { ideal: 1920 }, height: { ideal: 1080 }, frameRate: { max: 30 } },
  audio: { noiseSuppression: true },
};

/** Read the opt-in high-detail capture preference (Settings → 既定の動作). */
function hiresEnabled(): boolean {
  try {
    return localStorage.getItem("tebiki.capture.hires") === "1";
  } catch {
    return false;
  }
}

/** Pick MediaRecorder options: codec fallback + a video bitrate cap + a fixed narration audio rate. */
function pickRecorderOptions(): MediaRecorderOptions | undefined {
  if (typeof MediaRecorder === "undefined" || typeof MediaRecorder.isTypeSupported !== "function") return undefined;
  const mimeType = MIME_CANDIDATES.find((m) => MediaRecorder.isTypeSupported(m));
  const videoBitsPerSecond = hiresEnabled() ? VIDEO_BITRATE_HIRES : VIDEO_BITRATE_STANDARD;
  return mimeType
    ? { mimeType, videoBitsPerSecond, audioBitsPerSecond: AUDIO_BITRATE }
    : { videoBitsPerSecond, audioBitsPerSecond: AUDIO_BITRATE };
}

/** Construct a recorder with capped bitrate; fall back to the unconstrained recorder if rejected. */
function makeRecorder(stream: MediaStream): MediaRecorder {
  const opts = pickRecorderOptions();
  if (opts) {
    try {
      return new MediaRecorder(stream, opts);
    } catch {
      /* unsupported option set — fall through to the plain recorder (back-compat) */
    }
  }
  return new MediaRecorder(stream);
}

type Phase = "idle" | "recording" | "paused";

/** The just-recorded blob held for the confirmation gate (sent only on 『送る』). */
interface ReviewState {
  blob: Blob;
  url: string;
  /** accumulated ACTIVE recording time (pause excluded) */
  durationMs: number;
  /** false once <video> reports it can't play the blob on this device */
  playable: boolean;
}

export function CaptureVideo() {
  useSurface("field");
  const navigate = useNavigate();
  const closeTo = useCloseTo();
  const videoRef = useRef<HTMLVideoElement>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);

  // Active-time accounting (pause excluded): accumRef = confirmed active ms, segmentStartRef = current segment start.
  const accumRef = useRef(0);
  const segmentStartRef = useRef(0);

  const [phase, setPhase] = useState<Phase>("idle");
  const [elapsed, setElapsed] = useState(0); // accumulated active ms (display)
  const [camReady, setCamReady] = useState(false);
  const [camError, setCamError] = useState<string | null>(null);
  const [srMsg, setSrMsg] = useState(""); // one-shot screen-reader announcements (state changes / cap warning)
  const warnedRef = useRef(false); // fired the "near limit" announcement this recording

  /** Push a one-shot message to the polite live region (state changes shouldn't spam the SR). */
  function announce(m: string) {
    setSrMsg(m);
  }

  // Confirmation gate
  const [review, setReview] = useState<ReviewState | null>(null);
  const [sending, setSending] = useState(false);
  const [sendProgress, setSendProgress] = useState<number | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  // Draft manual created by a failed send — reused by 「もう一度送る」 so retries don't pile up drafts.
  const draftManualIdRef = useRef<string | null>(null);
  const [posterUrl, setPosterUrl] = useState<string | null>(null);
  const reviewUrlRef = useRef<string | null>(null);
  const posterUrlRef = useRef<string | null>(null);
  const shutterRef = useRef<HTMLButtonElement>(null);

  const isRecording = phase === "recording";
  const isPaused = phase === "paused";
  const isLive = isRecording || isPaused;
  // Keep the screen awake for the whole take (pause included — a sleeping screen kills the track).
  useWakeLock(isLive);
  const activeMs = () => accumRef.current + (phase === "recording" ? Date.now() - segmentStartRef.current : 0);
  const remainingMs = Math.max(0, VIDEO_MAX_ACTIVE_MS - elapsed);
  const nearLimit = isLive && remainingMs <= VIDEO_WARN_REMAINING_MS;

  // acquire camera + mic
  useEffect(() => {
    let cancelled = false;
    if (!navigator.mediaDevices?.getUserMedia) {
      setCamError("このデバイスではカメラを利用できません。");
      return;
    }
    navigator.mediaDevices
      .getUserMedia(cameraConstraints)
      .then((stream) => {
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          void videoRef.current.play().catch(() => {});
        }
        setCamReady(true);
      })
      .catch((e) => setCamError((e as Error).message || "カメラへのアクセスが拒否されました"));
    return () => {
      cancelled = true;
      // Stop any in-flight recorder WITHOUT firing handleStopped on a torn-down screen.
      const rec = recorderRef.current;
      if (rec && rec.state !== "inactive") {
        rec.onstop = null;
        try {
          rec.stop();
        } catch {
          /* already stopping */
        }
      }
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  // Revoke any preview/poster object URLs on unmount.
  useEffect(
    () => () => {
      if (reviewUrlRef.current) URL.revokeObjectURL(reviewUrlRef.current);
      if (posterUrlRef.current) URL.revokeObjectURL(posterUrlRef.current);
    },
    [],
  );

  // elapsed timer — runs only while actively recording; auto-finish at the active-time cap.
  useEffect(() => {
    if (phase !== "recording") return;
    const id = setInterval(() => {
      const a = activeMs();
      if (a >= VIDEO_MAX_ACTIVE_MS) {
        setElapsed(VIDEO_MAX_ACTIVE_MS); // clamp so the readout never overshoots 05:00
        finishRecording();
        return;
      }
      setElapsed(a);
      if (!warnedRef.current && VIDEO_MAX_ACTIVE_MS - a <= VIDEO_WARN_REMAINING_MS) {
        warnedRef.current = true;
        announce("まもなく録画の上限です。残り約30秒");
      }
    }, 250);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // The gate moves focus into itself when it opens; restore the shutter when it closes.
  useEffect(() => {
    if (!review) shutterRef.current?.focus();
  }, [review]);

  /** Wire up + start a recorder on the (already-live) streamRef. */
  function beginRecording() {
    const stream = streamRef.current;
    if (!stream) return;
    chunksRef.current = [];
    accumRef.current = 0;
    warnedRef.current = false;
    try {
      const rec = makeRecorder(stream);
      rec.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      rec.onstop = () => enterReview();
      rec.start(1000);
      recorderRef.current = rec;
      segmentStartRef.current = Date.now();
      setElapsed(0);
      setCamError(null);
      setPhase("recording");
    } catch {
      setCamError("録画を開始できませんでした。別のブラウザでお試しください。");
    }
  }

  /** Re-acquire the camera (e.g. after an iOS background suspend ended the track), then record. */
  async function reacquireThenRecord(retried: boolean) {
    try {
      const stream = await navigator.mediaDevices.getUserMedia(cameraConstraints);
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        void videoRef.current.play().catch(() => {});
      }
      setCamReady(true);
      const track = stream.getVideoTracks()[0];
      if (!track || track.readyState !== "live") {
        // Bound the retry so a device that keeps returning dead tracks can't ping-pong forever.
        if (retried) {
          setCamError("カメラを再取得できませんでした。ページを再読み込みしてください。");
          return;
        }
        void reacquireThenRecord(true);
        return;
      }
      setCamError(null);
      beginRecording();
    } catch (e) {
      setCamError((e as Error).message || "カメラへのアクセスが拒否されました");
    }
  }

  function startRecording() {
    const track = streamRef.current?.getVideoTracks()[0];
    if (!streamRef.current || !track || track.readyState !== "live") {
      setCamError("カメラを再取得します…");
      void reacquireThenRecord(false);
      return;
    }
    beginRecording();
  }

  function pauseRecording() {
    const rec = recorderRef.current;
    if (!rec || rec.state !== "recording") return;
    let paused = false;
    try {
      rec.pause();
      paused = true;
    } catch {
      /* this device can't pause — stay recording so the timeline accounting stays honest */
    }
    if (!paused) return;
    accumRef.current += Date.now() - segmentStartRef.current;
    setElapsed(accumRef.current);
    setPhase("paused");
    announce("録画を一時停止しました");
  }

  function resumeRecording() {
    const rec = recorderRef.current;
    if (!rec || rec.state !== "paused") return;
    if (accumRef.current >= VIDEO_MAX_ACTIVE_MS) {
      finishRecording();
      return;
    }
    let resumed = false;
    try {
      rec.resume();
      resumed = true;
    } catch {
      /* this device can't resume — stay paused rather than count un-recorded idle time */
    }
    if (!resumed) return;
    segmentStartRef.current = Date.now();
    setPhase("recording");
    announce("録画を再開しました");
  }

  function finishRecording() {
    const rec = recorderRef.current;
    if (!rec || rec.state === "inactive") return; // guards double-stop (no stoppingRef needed)
    if (rec.state === "recording") {
      accumRef.current += Date.now() - segmentStartRef.current;
      setElapsed(accumRef.current);
    }
    grabPoster(); // capture a live frame now (avoids iOS duration=Infinity black poster)
    try {
      rec.stop(); // → onstop → enterReview
    } catch {
      /* already stopping */
    }
  }

  function handleShutter() {
    if (phase === "idle") return startRecording();
    if (phase === "recording") return pauseRecording();
    if (phase === "paused") return resumeRecording();
  }

  /** Build the single blob from the accumulated chunks and open the confirmation gate. */
  function enterReview() {
    const type = chunksRef.current[0]?.type || "video/webm";
    const blob = new Blob(chunksRef.current, { type });
    chunksRef.current = [];
    const durationMs = accumRef.current; // ACTIVE time (pause excluded) — decision #4
    if (blob.size === 0 || durationMs < 300) {
      // Empty / accidental tap — never ship an empty clip to Gemini.
      clearReviewUrls(); // drop any poster grabbed during finishRecording()
      setCamError("録画に失敗しました。もう一度撮影してください。");
      setPhase("idle");
      setReview(null);
      return;
    }
    const url = URL.createObjectURL(blob);
    if (reviewUrlRef.current) URL.revokeObjectURL(reviewUrlRef.current);
    reviewUrlRef.current = url;
    setPhase("idle");
    setReview({ blob, url, durationMs, playable: true });
  }

  /** Decorative poster (last live frame) so the gate isn't a black box on iOS. NOT a keyframe. */
  function grabPoster() {
    const v = videoRef.current;
    if (!v || !v.videoWidth || !v.videoHeight) return;
    try {
      const c = document.createElement("canvas");
      c.width = v.videoWidth;
      c.height = v.videoHeight;
      const ctx = c.getContext("2d");
      if (!ctx) return;
      ctx.drawImage(v, 0, 0, c.width, c.height);
      c.toBlob(
        (b) => {
          if (!b) return;
          const url = URL.createObjectURL(b);
          if (posterUrlRef.current) URL.revokeObjectURL(posterUrlRef.current);
          posterUrlRef.current = url;
          setPosterUrl(url);
        },
        "image/jpeg",
        0.8,
      );
    } catch {
      /* tainted/not ready — gate falls back to text */
    }
  }

  function clearReviewUrls() {
    if (reviewUrlRef.current) {
      URL.revokeObjectURL(reviewUrlRef.current);
      reviewUrlRef.current = null;
    }
    if (posterUrlRef.current) {
      URL.revokeObjectURL(posterUrlRef.current);
      posterUrlRef.current = null;
    }
    setPosterUrl(null);
  }

  /**
   * 『送る』 — upload directly (init → PUT → complete), falling back to the offline queue. If both
   * fail, stay on the gate with the take intact so the user can retry instead of losing it. Keeps
   * the recorded blob under the (real or local) manualId so the keyframe picker can extract frames
   * without re-downloading the video.
   */
  async function sendReview() {
    if (!review || sending) return;
    setSending(true);
    setSendError(null);
    setSendProgress(0);
    const result = await sendVideoOrQueue(review.blob, {
      manualId: draftManualIdRef.current,
      onProgress: setSendProgress,
    });
    setSendProgress(null);
    if (result.status === "failed") {
      draftManualIdRef.current = result.manualId;
      setSending(false);
      setSendError(result.error);
      return;
    }
    draftManualIdRef.current = null;
    rememberCaptureOrigin(result.manualId, "video");
    clearReviewUrls();
    navigate(`/processing/${result.manualId}`);
  }

  /** 『撮り直す』 — drop the take, return to the live viewfinder (stream stays live). */
  function retake() {
    setSendError(null);
    draftManualIdRef.current = null;
    clearReviewUrls();
    setReview(null);
    accumRef.current = 0;
    setElapsed(0);
    setPhase("idle");
  }

  /** 『破棄』 — discard the take entirely and leave the capture screen. */
  function discard() {
    setSendError(null);
    draftManualIdRef.current = null;
    clearReviewUrls();
    setReview(null);
    navigate(closeTo);
  }

  return (
    <div className="cap-shell" style={{ height: "100dvh", display: "flex", flexDirection: "column" }}>
      <h1 className="sr-only">動画撮影</h1>
      <span className="sr-only" role="status" aria-live="polite">
        {srMsg}
      </span>
      <div className="viewfinder" aria-hidden={review ? true : undefined}>
        <span className="vf-corner vf-tl" />
        <span className="vf-corner vf-tr" />
        <span className="vf-corner vf-bl" />
        <span className="vf-corner vf-br" />

        <video
          ref={videoRef}
          muted
          playsInline
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            opacity: camReady ? 1 : 0,
            transition: "opacity .3s",
          }}
        />
        {!camReady && !review ? (
          <div className="frame" style={{ width: "min(46vw,300px)", border: 0, background: "none", boxShadow: "none" }}>
            <div className="subj" style={{ boxShadow: "0 30px 70px -10px rgba(0,0,0,.7)" }} />
          </div>
        ) : null}

        <div className="topstrip" style={{ position: "absolute", top: 0, left: 0, right: 0, zIndex: 10, display: "flex", alignItems: "center", gap: 12, padding: "max(16px, env(safe-area-inset-top)) max(20px, env(safe-area-inset-right)) 16px max(20px, env(safe-area-inset-left))" }}>
          <Link
            className="btn btn--ghost"
            to={closeTo}
            aria-label="閉じる"
            tabIndex={review ? -1 : undefined}
            style={{ minHeight: 56, minWidth: 56, padding: "8px 14px", borderRadius: 12, background: "rgba(0,0,0,.4)" }}
          >
            <Icon name="close" size={20} />
          </Link>
          <span className="grow" />
          <span
            className="timer"
            style={{ fontFamily: "var(--font-mono)", fontSize: 18, background: "rgba(0,0,0,.5)", padding: "6px 12px", borderRadius: 999, display: "flex", alignItems: "center", gap: 8 }}
          >
            {isRecording ? <span className="rec-dot" aria-hidden="true" /> : isPaused ? <Icon name="pause" size={14} aria-hidden /> : null}
            <span style={nearLimit ? { color: "var(--rec)" } : undefined}>{mmss(elapsed)}</span>
            <span style={{ opacity: 0.55, fontSize: 13 }}>/ {mmss(VIDEO_MAX_ACTIVE_MS)}</span>
          </span>
        </div>

        {!review ? (
          <div
            className="guide reveal d2"
            style={{ position: "absolute", left: 0, right: 0, top: "46%", transform: "translateY(-50%)", textAlign: "center", pointerEvents: "none", padding: "0 30px" }}
          >
            {camError ? (
              <div style={{ display: "inline-block" }}>
                <div className="kicker" style={{ color: "var(--danger)", letterSpacing: "0.24em" }}>
                  カメラを準備できません
                </div>
                <h2 className="display jp" style={{ fontSize: "clamp(22px,5vw,34px)", lineHeight: 1.15, margin: "10px 0 0", textShadow: "0 2px 18px rgba(0,0,0,.55)" }}>
                  権限を<span className="hl">確認</span>してください。
                </h2>
                <p className="mono" style={{ marginTop: 12, fontSize: 12, color: "var(--danger)", letterSpacing: "0.04em" }}>
                  {camError}
                </p>
              </div>
            ) : isPaused ? (
              <div className="mono" style={{ fontSize: 13, letterSpacing: "0.2em", color: "var(--ink)", textShadow: "0 2px 14px rgba(0,0,0,.65)" }}>
                <Icon name="pause" size={14} aria-hidden /> 一時停止中 — 中央で<span className="hl">再開</span>、右で完了
              </div>
            ) : isRecording ? (
              <div className="mono" style={{ fontSize: 13, letterSpacing: "0.2em", color: "var(--ink)", textShadow: "0 2px 14px rgba(0,0,0,.65)" }}>
                <span className="rec-dot" aria-hidden="true" style={{ display: "inline-block", verticalAlign: "middle", marginRight: 8 }} />
                実況を録音中 — 区切りで<span className="hl">一時停止</span>OK
              </div>
            ) : (
              <div style={{ position: "relative", display: "inline-block", padding: "6px 10px" }}>
                {/* soft vignette so the editorial type stays legible over the live viewfinder */}
                <span
                  aria-hidden="true"
                  style={{ position: "absolute", inset: "-34% -28%", background: "radial-gradient(58% 58% at 50% 50%, rgba(8,9,12,.55), transparent 72%)", zIndex: -1 }}
                />
                <div className="kicker" style={{ color: "var(--hi)", letterSpacing: "0.24em" }}>
                  話しながら撮影
                </div>
                <h2 className="display jp" style={{ fontSize: "clamp(24px,5.6vw,40px)", lineHeight: 1.12, letterSpacing: "-0.01em", margin: "10px 0 0", textShadow: "0 2px 20px rgba(0,0,0,.6)" }}>
                  <span className="hl">声で実況</span>しながら、
                  <br />
                  撮るだけ。
                </h2>
                <p className="mono" style={{ marginTop: 14, fontSize: 12, letterSpacing: "0.05em", color: "var(--ink)", opacity: 0.82 }}>
                  途中で <span style={{ color: "var(--hi)" }}>一時停止</span> → 再開で1本に。区切りは AI が検出。
                </p>
              </div>
            )}
          </div>
        ) : null}
      </div>

      {!review ? (
        <div
          className="controls"
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            padding: "22px 24px calc(26px + env(safe-area-inset-bottom))",
            display: "grid",
            gridTemplateColumns: "1fr auto 1fr",
            alignItems: "center",
            gap: 16,
            background: "linear-gradient(0deg,rgba(0,0,0,.7),transparent)",
          }}
        >
          <div className="narr" style={narrStyle}>
            <div className="wave" aria-hidden="true" style={{ opacity: isRecording ? 1 : 0.35 }}>
              {Array.from({ length: 9 }, (_, i) => (
                <i key={i} />
              ))}
            </div>
            <span className="mono" style={{ fontSize: 12, color: isPaused ? "var(--muted)" : "var(--hi)" }}>
              {isRecording ? "録音中" : isPaused ? "一時停止" : "待機中"}
            </span>
          </div>
          <button
            ref={shutterRef}
            className={`shutter${isRecording ? " shutter--rec" : ""}${isPaused ? " shutter--paused" : ""}`}
            aria-label={phase === "idle" ? "録画開始" : phase === "recording" ? "一時停止" : "録画を再開"}
            aria-pressed={isLive}
            onClick={handleShutter}
          />
          <div style={{ justifySelf: "end" }}>
            <Btn
              variant="hi"
              lg
              icon={<Icon name="check" size={18} />}
              onClick={finishRecording}
              aria-label="撮影を完了"
              style={{ visibility: isLive ? "visible" : "hidden" }}
            >
              完了
            </Btn>
          </div>
        </div>
      ) : null}

      {review ? (
        <ReviewGate
          url={review.url}
          posterUrl={posterUrl}
          playable={review.playable}
          onUnplayable={() => setReview((r) => (r ? { ...r, playable: false } : r))}
          durationMs={review.durationMs}
          sending={sending}
          progress={sendProgress}
          error={sendError}
          onSend={() => void sendReview()}
          onRetake={retake}
          onDiscard={discard}
        />
      ) : null}
    </div>
  );
}

const narrStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  background: "rgba(0,0,0,.42)",
  padding: "10px 14px",
  borderRadius: 14,
  backdropFilter: "blur(8px)",
};
